import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { GridDataSource } from '@mui/x-data-grid-pro';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	useGetAssetPageSummaryQuery,
	useLazyGetAssetsQuery,
	useLazyGetNonLeasedAssetsQuery,
} from '../../api/asset.api';
import { useLazyGetContractsQuery } from '../../api/contract.api';
import type { Asset } from '../../api/models/asset.model.ts';
import type { AssetPageSummary } from '../../api/models/assetPageSummary.model.ts';
import type { Contract } from '../../api/models/contract.model';
import type { NonLeasedAsset } from '../../api/models/nonLeasedAsset.model.ts';
import { useGetCurrentUserQuery, useGetSavedSearchesQuery } from '../../api/user.api.ts';
import { CustomTabPanel } from '../../components/CustomTabPanel/CustomTabPanel';
import {
	contractColumns,
	mobileContractColumns,
} from '../../components/DataGrid/columnDefinitions.tsx';
import DataGridProComponent from '../../components/DataGrid/DataGridProComponent';
import { ContractsDataGridType } from '../../components/DataGrid/DataGridTypes.ts';
import InfoCard from '../../components/InfoCard/InfoCard';
import { NON_LEASED_ASSET_TYPE } from '../../constants/logic.ts';
import {
	ASSETS,
	CONTRACT_NO,
	CONTRACTS,
	CONTRACTS_AND_ASSETS,
	NON_LEASED_ASSETS,
	setTableMessage,
} from '../../constants/messages.tsx';
import { selectAccount } from '../../redux/slices/accountSlice.ts';
import { clearSelectedAssetSerialNo } from '../../redux/slices/assetSlice.ts';
import { clearSelectedContractDetailsViewId } from '../../redux/slices/contractSlice.ts';
import { selectAssetGridRefreshTrigger } from '../../redux/slices/udfUpdateSlice.ts';
import { type RootState } from '../../redux/store';
import Assets from './Assets/Assets';
import ContractDetails from './ContractDetails/ContractDetails';
import './Contracts.scss';

interface Tab {
	key: string;
	title: string;
	closeable: boolean;
	contract?: Contract;
}

const Contracts: React.FC = () => {
	const account = useSelector(selectAccount)!;
	const dispatch = useDispatch();

	const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
	const activeFilters = useSelector((state: RootState) => state.filters.activeFilters);
	const [currentContractsData, setCurrentContractsData] = useState<Contract[]>([]);
	const [currentAssetsData, setCurrentAssetsData] = useState<Asset[]>([]);
	const [currentNonLeasedAssetsData, setCurrentNonLeasedAssetsData] = useState<NonLeasedAsset[]>(
		[]
	);
	const [dataIsLoading, setDataIsLoading] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const assetGridRefreshTrigger = useSelector(selectAssetGridRefreshTrigger);

	const [getContracts] = useLazyGetContractsQuery();

	const { data: user } = useGetCurrentUserQuery();

	const { data: savedSearches } = useGetSavedSearchesQuery(undefined, {
		skip: !user || !user.userId,
	});

	const contractDataSource: GridDataSource = useMemo(
		() => ({
			getRows: async ({ paginationModel, sortModel }) => {
				if (!account.accountId) {
					// Skip API call if accountId is not set
					return { rows: [], rowCount: 0 };
				}
				setDataIsLoading(true);
				// Refetch with new pagination
				const contractQueryParams = {
					accountId: account.accountId,
					sourceId: account.sourceId,
					accountTypeCode: account.accountTypeCode,
					filters: activeFilters,
					pageSize: paginationModel?.pageSize ?? 10,
					currentPage: paginationModel?.page ?? 0,
					sortDirection: sortModel?.[0]?.sort || '',
					sortField: sortModel?.[0]?.field || '',
				};

				const result = await getContracts(contractQueryParams, true).unwrap();
				setCurrentContractsData(result?.content || []);
				setDataIsLoading(false);
				return {
					rows: result?.content ?? [],
					rowCount: result?.totalElements ?? 0,
				};
			},
		}),
		[account, activeFilters]
	);

	const [getAssets] = useLazyGetAssetsQuery();

	const assetDataSource: GridDataSource = useMemo(
		() => ({
			getRows: async ({ paginationModel, sortModel }) => {
				// Refetch with new pagination
				const assetQueryParams = {
					accountId: account.accountId,
					sourceId: account.sourceId,
					accountTypeCode: account.accountTypeCode,
					filters: activeFilters,
					pageSize: paginationModel?.pageSize ?? 10,
					currentPage: paginationModel?.page ?? 0,
					sortDirection: sortModel?.[0]?.sort || '',
					sortField: sortModel?.[0]?.field || '',
				};
				setDataIsLoading(true);

				const result = await getAssets(assetQueryParams, false).unwrap();
				setCurrentAssetsData(result?.content || []);
				setDataIsLoading(false);
				return {
					rows: result?.content ?? [],
					rowCount: result?.totalElements ?? 0,
				};
			},
		}),
		[account, activeFilters, assetGridRefreshTrigger]
	);

	const [getNonLeasedAssets] = useLazyGetNonLeasedAssetsQuery();

	const nonLeasedAssetDataSource: GridDataSource = useMemo(
		() => ({
			getRows: async ({ paginationModel, sortModel }) => {
				// Refetch with new pagination
				const assetQueryParams = {
					accountId: account.accountId,
					sourceId: account.sourceId,
					accountTypeCode: account.accountTypeCode,
					filters: activeFilters,
					pageSize: paginationModel?.pageSize ?? 10,
					currentPage: paginationModel?.page ?? 0,
					sortDirection: sortModel?.[0]?.sort || '',
					sortField: sortModel?.[0]?.field || '',
				};
				setDataIsLoading(true);

				const result = await getNonLeasedAssets(assetQueryParams, true).unwrap();
				setCurrentNonLeasedAssetsData(result?.content || []);
				setDataIsLoading(false);
				return {
					rows: result?.content ?? [],
					rowCount: result?.totalElements ?? 0,
				};
			},
		}),
		[account, activeFilters, refreshTrigger]
	);

	const {
		data: assetPageSummary = {} as AssetPageSummary,
		isLoading: assetPageSummaryLoading,
		isError: assetIsError, //NOTE: unused causing ts errors. Will be used later
	} = useGetAssetPageSummaryQuery(
		{
			accountId: account?.accountId,
			accountTypeCode: account?.accountTypeCode,
			sourceId: account?.sourceId,
			filters: activeFilters,
		},
		{
			skip: !account?.accountId || !account?.accountTypeCode || !account?.sourceId,
		}
	);

	const columns = isMobile ? mobileContractColumns : contractColumns;

	const selectedContractDetailsViewId = useSelector(
		(state: RootState) => state.contracts.selectedContractDetailsViewId
	);

	React.useEffect(() => {
		if (selectedContractDetailsViewId) {
			const foundContract = currentContractsData.find(
				(c) => c.contractNumber === selectedContractDetailsViewId
			);
			if (foundContract) {
				handleContractClick(foundContract);
			}
		}
	}, [selectedContractDetailsViewId]);

	// Tab Specs
	const [tabs, setTabs] = useState([
		{ key: 'contracts', title: CONTRACTS, closeable: false },
		{ key: 'assets', title: ASSETS, closeable: false },
		{ key: 'nonLeasedAssets', title: NON_LEASED_ASSETS, closeable: false },
	]);
	const [activeTabKey, setActiveTabKey] = useState('contracts');

	// Handler for contract link click
	const handleContractClick = useCallback(
		(contract: Contract) => {
			const key = `contract-${contract.contractNumber}`;
			if (!tabs.find((tab) => tab.key === key)) {
				setTabs((prev) => [
					...prev,
					{
						key,
						title: `Contract ${contract.contractNumber}`,
						closeable: true,
						contract, // Store contract data in tab
					},
				]);
			}
			setActiveTabKey(key);
		},
		[tabs]
	);

	// Helper to render contracts tab
	const renderContractsTabView = () => {
		return (
			<Box sx={{ width: '100%', mt: 3, padding: 0 }}>
				{/* {contractsLoading && <div>Loading...</div>} */}
				{setTableMessage(CONTRACT_NO)}

				<DataGridProComponent
					columns={columns}
					dataGridType={ContractsDataGridType}
					dataSource={contractDataSource}
					dataIsLoading={dataIsLoading}
				/>
			</Box>
		);
	};

	// Helper to render assets tab
	const renderAssetsTabView = () => (
		<Box sx={{ width: '100%', mt: 3, padding: 0 }}>
			{/* {assetsLoading && <div>Loading...</div>} */}
			<Assets
				assetsDataSource={assetDataSource}
				assetData={currentAssetsData}
				dataIsLoading={dataIsLoading}
				refreshTrigger={assetGridRefreshTrigger}
			/>
		</Box>
	);

	const handleNonLeasedAssetsRefresh = () => {
		setRefreshTrigger((prev) => prev + 1);
	};

	const renderNonLeasedAssetsTabView = () => (
		<Box sx={{ width: '100%', mt: 3, padding: 0 }}>
			{/* {assetsLoading && <div>Loading...</div>} */}
			<Assets
				assetsDataSource={nonLeasedAssetDataSource}
				assetData={currentNonLeasedAssetsData}
				dataIsLoading={dataIsLoading}
				assetType={NON_LEASED_ASSET_TYPE}
				onRefresh={handleNonLeasedAssetsRefresh}
			/>
		</Box>
	);

	// Helper to render contract detail tab
	const renderContractDetailTab = (contract: Contract) => {
		return (
			<Box sx={{ width: '100%', mt: 3, padding: 0 }}>
				<ContractDetails contract={contract} />
			</Box>
		);
	};

	// Handler to close a tab
	const handleCloseTab = (key: string) => {
		setTabs((prev) => prev.filter((tab) => tab.key !== key));
		// If closing the active tab, switch to previous or default
		if (activeTabKey === key) {
			const idx = tabs.findIndex((tab) => tab.key === key);
			const newActive = tabs[idx - 1]?.key || 'contracts';
			setActiveTabKey(newActive);
		}

		dispatch(clearSelectedContractDetailsViewId());
		dispatch(clearSelectedAssetSerialNo());
	};

	// Render the correct view for each tab
	const renderTabView = (tab: Tab) => {
		if (tab.key === 'contracts') return renderContractsTabView();
		if (tab.key === 'assets') return renderAssetsTabView();
		if (tab.key === 'nonLeasedAssets') return renderNonLeasedAssetsTabView();
		if (tab.key.startsWith('contract-')) {
			return renderContractDetailTab(tab?.contract || ({ contractNumber: '', id: 0 } as Contract));
		}
		return null;
	};

	const assetPageSummaryValues = (key: keyof AssetPageSummary) => {
		if (assetIsError) return 'Error';
		return assetPageSummaryLoading ? 'Loading...' : (assetPageSummary[key] ?? 'N/A');
	};

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
				<Typography
					variant="h4"
					component="h1"
					sx={{
						color: (theme) => theme.palette.brand.primary,
						fontWeight: 'bold',
						ml: '0.75rem',
					}}
				>
					{CONTRACTS_AND_ASSETS}
				</Typography>
			</Box>
			<Grid
				container
				spacing={2}
				sx={{
					mb: 3,
					display: 'flex',
					flexDirection: 'row',
					flexWrap: 'wrap',
					justifyContent: isMobile ? 'space-evenly' : 'flex-start',
					alignItems: 'stretch',
					margin: isMobile ? '0 auto' : undefined,
				}}
			>
				<Grid size={{ xs: 6, md: 4, lg: 3 }}>
					<InfoCard
						heading={assetPageSummaryValues('totalContracts')}
						subheading="Active Contracts"
						sx={{ flexGrow: 1 }}
					></InfoCard>
				</Grid>
				<Grid size={{ xs: 6, md: 4, lg: 3 }}>
					<InfoCard
						heading={assetPageSummaryValues('totalAssets')}
						subheading="Active Assets"
						sx={{ flexGrow: 1 }}
					></InfoCard>
				</Grid>
				<Grid size={{ xs: 6, md: 4, lg: 3 }}>
					<InfoCard
						heading={assetPageSummaryValues('totalDistinctAddresses')}
						subheading="Asset Locations"
						sx={{ flexGrow: 1 }}
					></InfoCard>
				</Grid>
				<Grid size={{ xs: 6, md: 4, lg: 3 }}>
					<InfoCard
						heading={savedSearches?.length ?? 'N/A'}
						subheading="Saved Searches"
						sx={{ flexGrow: 1 }}
					></InfoCard>
				</Grid>
				{assetPageSummary.accounts != null && (
					<Grid size={{ xs: 6, md: 4, lg: 3 }}>
						<InfoCard
							heading={assetPageSummary.accounts}
							subheading="Accounts"
							sx={{ flexGrow: 1 }}
						/>
					</Grid>
				)}
			</Grid>

			<Box sx={{ width: '100%', mb: 3, display: 'flex', flexDirection: 'column' }}>
				<Tabs
					value={activeTabKey}
					onChange={(_, v) => setActiveTabKey(v)}
					aria-label="contracts-assets-tabs"
					variant="scrollable"
					scrollButtons="auto"
					sx={{ float: 'left', borderBottom: '1px solid' }}
				>
					{tabs.map((tab) => (
						<Tab
							key={tab.key}
							label={
								<span className="contract-tab">
									{tab.title}
									{tab.closeable && (
										<CloseIcon
											fontSize="small"
											sx={{ ml: 1, cursor: 'pointer' }}
											onClick={(e) => {
												e.stopPropagation();
												handleCloseTab(tab.key);
											}}
										/>
									)}
								</span>
							}
							value={tab.key}
							id={`tab-${tab.key}`}
							aria-controls={`tabpanel-${tab.key}`}
							data-testid={`tab-${tab.key}`}
						/>
					))}
				</Tabs>
				{tabs.map((tab) => (
					<CustomTabPanel
						key={tab.key}
						selected={activeTabKey}
						value={tab.key}
						className="tab-container"
					>
						{renderTabView(tab)}
					</CustomTabPanel>
				))}
			</Box>
		</Box>
	);
};

export default Contracts;
